import { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { fetchSignedUrl } from "@/lib/fileQueries";

const VIDEO_EXTENSIONS = new Set(["mp4", "webm"]);

/**
 * Master v5 Prompt 24 — plays back a Rehearsal's uploaded recording.
 * Fetches its own fresh signed URL on mount (never a cached one), same
 * pattern as FileRoomRenderer.jsx. The recording is Bunny Storage (not
 * Vimeo, not Bunny Stream) so this is a plain HTML5 <video>/<audio> tag
 * against the signed download URL, not an embed iframe.
 */
export default function RehearsalPlayback({ workspaceId, fasId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fasId) return;
    fetchSignedUrl(workspaceId, fasId).then(setData).catch((e) => setError(e.body?.detail || e.message));
  }, [workspaceId, fasId]);

  if (!fasId) return null;
  if (error) {
    return (
      <div className="flex items-center gap-2 text-terracotta text-sm" data-testid="rehearsal-playback-error">
        <AlertTriangle size={14} /> {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-ink-soft text-sm" data-testid="rehearsal-playback-loading">
        <Loader2 size={14} className="animate-spin" /> Loading recording…
      </div>
    );
  }

  const isVideo = VIDEO_EXTENSIONS.has(data.extension);
  return (
    <div className="bg-ink/5 rounded-lg overflow-hidden p-2" data-testid="rehearsal-playback">
      {isVideo ? (
        <video src={data.signed_url} controls className="w-full max-h-[50vh] rounded-md" data-testid="rehearsal-playback-video" />
      ) : (
        <audio src={data.signed_url} controls className="w-full" data-testid="rehearsal-playback-audio" />
      )}
    </div>
  );
}
